import type { FC, ReactNode } from 'react';
import { Navigate } from 'react-router';
import PropTypes from 'prop-types';
import useAuth from '../hooks/useAuth';
import { getAuthenticatedLandingPath } from '../utils/permissionLanding';

interface TwoFactorAuthGuardProps {
  children: ReactNode;
}

const TwoFactorAuthGuard: FC<TwoFactorAuthGuardProps> = ({ children }) => {
  const { isAuthenticated, isInitialized, user } = useAuth();
  const is2FAEnabled = localStorage.getItem('is2FAEnabled') === 'true';
  const isOtpVerified = localStorage.getItem('isOtpVerified') === 'true';
  const inTwoFactorHandoff = is2FAEnabled && !isOtpVerified;
  const landingPath = getAuthenticatedLandingPath(user);

  if (!isInitialized) {
    return null;
  }

  // Fully authenticated (OTP done or no 2FA) → app landing.
  if (isAuthenticated && !inTwoFactorHandoff) {
    return <Navigate to={landingPath} replace />;
  }

  // Mid-2FA: stay on OTP page even if a stale accessToken remains from a prior session.
  if (inTwoFactorHandoff) {
    return <>{children}</>;
  }

  // Stale token without an active 2FA handoff → leave the 2FA route.
  if (localStorage.getItem('accessToken')) {
    return <Navigate to={landingPath} replace />;
  }

  return <Navigate to="/login" replace />;
};

TwoFactorAuthGuard.propTypes = {
  children: PropTypes.node,
};

export default TwoFactorAuthGuard;
